import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Map as MapIcon, List } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { normalizeSearch } from '../../utils/formatters';
import type { StandardItem, StandardPlace } from '../../types/standard';
import type { ExplorePlace } from './ExploreMapView';
import { standardClient } from '../../lib/standard';
import { navigationClient } from '../../lib/navigation';
import { useHeader } from '../../context/HeaderContext';
import ExploreMapView from './ExploreMapView';
import { ExploreListView } from './ExploreListView';
import { ExploreFilterDrawer } from './ExploreFilterDrawer';
import type { ExploreFilterState } from './ExploreFilterDrawer';
import { SearchAndFilterBar } from '../standard/SearchAndFilterBar';
import { SectionHeader } from '../layout/SectionHeader';
import { DetailCard } from '../standard/DetailCard';
import { PlaceCard } from '../standard/PlaceCard';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { useAirtableQuery } from '../../hooks/useAirtableQuery';
import type { SupportedLanguages } from '../../i18n';
import { resolveColor } from '../../utils/colorUtils';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { getTranslatedText as translate } from '../../utils/translationHelpers';
import ErrorBoundary from '../analytics/ErrorBoundary';

const EXPLORE_NAMES: Record<string, string> = {
  fr: 'Explorer',
  en: 'Explore',
  es: 'Explorar',
  it: 'Esplorare',
  pt: 'Explorar',
  de: 'Erkunden',
  nl: 'Verkennen',
  ja: '探索',
  zh: '探索',
  ru: 'Обзор',
};

const ExploreSection: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { setTitle } = useHeader();
  const mapInstanceRef = useRef<google.maps.Map | null>(null);

  const [view, setView] = useState<'map' | 'list'>('map');
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState<ExploreFilterState>({ sectionIds: [], categories: [], types: [], priceFilter: [] });
  const [selectedItem, setSelectedItem] = useState<StandardItem | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<StandardPlace | null>(null);
  const [selectedSectionIdForDetail, setSelectedSectionIdForDetail] = useState<string>('');

  const { data: config, isLoading: configLoading } = useAirtableQuery.useConfig();
  const { data: translations, isLoading: translationsLoading } = useAirtableQuery.useTranslations(i18n.language as SupportedLanguages);

  const { data: navigation } = useQuery({
    queryKey: ['navigation'],
    queryFn: () => navigationClient.getNavigation(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  const standardSections = useMemo(() => {
    if (!navigation) return [];
    return navigation.filter(item =>
      item.type_rubrique === 'standard' && item.include_in_explore !== false
    );
  }, [navigation]);

  const { data: allSectionItems = [], isLoading: itemsLoading } = useQuery({
    queryKey: ['explore-all-standard-items', standardSections.map(s => s.id).join(',')],
    queryFn: async () => {
      if (!standardSections.length) return [];
      const results = await Promise.all(
        standardSections.map(async (section) => {
          try {
            const items = await standardClient.getItems(section.id);
            return items.map(item => ({
              ...item,
              _sectionId: section.id,
              _sectionName: section.names[i18n.language as SupportedLanguages] || section.names.fr,
              _sectionNameFr: section.names.fr,
            }));
          } catch {
            return [];
          }
        })
      );
      return results.flat();
    },
    enabled: standardSections.length > 0,
    staleTime: 2 * 60 * 1000,
    cacheTime: 10 * 60 * 1000
  });

  useEffect(() => {
    setTitle(EXPLORE_NAMES[i18n.language] || EXPLORE_NAMES.fr);
  }, [i18n.language, setTitle]);

  const sectionColorMap = useMemo(() => {
    if (!config) return new Map<string, string>();
    const colors = config.colors_map?.split(',').map((c: string) => c.trim()) || [];
    const map = new Map<string, string>();
    standardSections.forEach((section, index) => {
      map.set(section.id, colors[index % colors.length] || config['medium-color']);
    });
    return map;
  }, [standardSections, config]);

  const sectionInfos = useMemo(() => {
    return standardSections.map((section) => {
      const count = allSectionItems.filter((item: any) => item._sectionId === section.id).length;
      return {
        id: section.id,
        name: section.names[i18n.language as SupportedLanguages] || section.names.fr,
        color: sectionColorMap.get(section.id) || '#6B7280',
        itemCount: count,
      };
    });
  }, [standardSections, allSectionItems, i18n.language, sectionColorMap]);

  const filteredItems = useMemo(() => {
    return allSectionItems.filter((item: any) => {
      const sectionMatch = filters.sectionIds.length === 0 || filters.sectionIds.includes(item._sectionId);
      const categoryMatch = filters.categories.length === 0 || filters.categories.includes(item.category);
      const typeMatch = filters.types.length === 0 || filters.types.includes(item.type);
      const priceMatch = filters.priceFilter.length === 0 || filters.priceFilter.includes(item.price?.trim() || '');
      const q = normalizeSearch(searchQuery.trim());
      const searchMatch =
        !q ||
        normalizeSearch(item.name).includes(q) ||
        normalizeSearch(item.fulladress || '').includes(q) ||
        normalizeSearch(item.category || '').includes(q) ||
        normalizeSearch(item.type || '').includes(q) ||
        normalizeSearch(item._sectionName || '').includes(q);
      return sectionMatch && categoryMatch && typeMatch && priceMatch && searchMatch;
    });
  }, [allSectionItems, filters, searchQuery]);

  const explorePlaces = useMemo((): ExplorePlace[] => {
    const placeMap = new Map<string, ExplorePlace>();
    filteredItems.forEach((item: any) => {
      const key = `${item._sectionId}_${item.name}`;
      if (!placeMap.has(key)) {
        placeMap.set(key, {
          name: item.name,
          latitude: item.latitude,
          longitude: item.longitude,
          distance: item.distance,
          timewalk: item.timewalk,
          fulladress: item.fulladress,
          items: [],
          sectionId: item._sectionId,
          sectionName: item._sectionName,
          sectionColor: sectionColorMap.get(item._sectionId) || '#6B7280',
        });
      }
      placeMap.get(key)!.items.push(item);
    });
    return Array.from(placeMap.values());
  }, [filteredItems, sectionColorMap]);

  const categoryColorsForDetail = useMemo(() => {
    if (!config) return new Map<string, string>();
    const colors = config.colors_map?.split(',').map((c: string) => c.trim()) || [];
    const categories = Array.from(new Set(filteredItems.map((item: any) => item.category)));
    const map = new Map<string, string>();
    categories.forEach((cat, idx) => {
      map.set(cat, colors[idx % colors.length] || config['medium-color']);
    });
    return map;
  }, [config, filteredItems]);

  const mainLocation = useMemo(() => ({
    name: config.name,
    latitude: config.latitude,
    longitude: config.longitude,
    fulladress: config.fulladress
  }), [config.name, config.latitude, config.longitude, config.fulladress]);

  const activeFiltersCount =
    filters.sectionIds.length +
    filters.categories.length +
    filters.types.length +
    filters.priceFilter.length;

  const listItems = useMemo(() => {
    const items = filteredItems.map((item: any) => ({
      ...item,
      sectionId: item._sectionId,
      sectionName: item._sectionName,
      sectionColor: sectionColorMap.get(item._sectionId) || '#6B7280',
    }));
    items.sort((a: any, b: any) => {
      const da = parseFloat(a.distance) || Infinity;
      const db = parseFloat(b.distance) || Infinity;
      return da - db;
    });
    return items;
  }, [filteredItems, sectionColorMap]);

  const sectionLegend = useMemo(() => {
    return sectionInfos.map(s => ({ id: s.id, name: s.name, color: s.color }));
  }, [sectionInfos]);

  const handleFiltersChange = (newFilters: ExploreFilterState) => {
    setFilters(newFilters);
    setSelectedItem(null);
    setSelectedPlace(null);
  };

  const handleSectionFilter = (sectionId: string) => {
    setFilters(prev => {
      const isAlready = prev.sectionIds.includes(sectionId);
      const next = isAlready
        ? prev.sectionIds.filter(s => s !== sectionId)
        : [...prev.sectionIds, sectionId];
      return { ...prev, sectionIds: next, categories: [], types: [] };
    });
    setSelectedItem(null);
    setSelectedPlace(null);
    analyticsService.trackEvent({
      event_type: 'filter',
      event_name: 'explore_section_filter',
      event_data: { section_id: sectionId }
    });
  };

  const handleSectionCategoryFilter = (sectionId: string, category: string) => {
    setFilters(prev => ({ ...prev, sectionIds: [sectionId], categories: [category], types: [] }));
    setSelectedItem(null);
    setSelectedPlace(null);
  };

  const handleSectionCategoryTypeFilter = (sectionId: string, category: string, type: string) => {
    setFilters(prev => ({ ...prev, sectionIds: [sectionId], categories: [category], types: [type] }));
    setSelectedItem(null);
    setSelectedPlace(null);
  };

  const handleBackClick = () => navigate('/');

  const handleItemClick = (item: StandardItem) => {
    const meta = item as any;

    if (view === 'map' && mapInstanceRef.current && item.latitude && item.longitude) {
      mapInstanceRef.current.panTo({ lat: parseFloat(item.latitude), lng: parseFloat(item.longitude) });
      const currentZoom = mapInstanceRef.current.getZoom() || 12;
      if (currentZoom < 15) {
        mapInstanceRef.current.setZoom(15);
      }
    }

    setSelectedSectionIdForDetail(meta._sectionId || '');
    setSelectedItem(item);
    setSelectedPlace(null);
    analyticsService.trackEvent({
      event_type: 'interaction',
      event_name: 'explore_item_click',
      event_data: { item_name: item.name, section_id: meta._sectionId }
    });
  };

  const handlePlaceClick = (place: ExplorePlace) => {
    setSelectedPlace(place);
    setSelectedItem(null);
    setSelectedSectionIdForDetail(place.sectionId);
  };

  const handleDetailClose = () => {
    setSelectedItem(null);
    setSelectedPlace(null);
  };

  const handleItemChange = (newItem: StandardItem) => {
    setSelectedItem(newItem);
  };

  const handleCategorySelect = (category: string) => {
    const meta = selectedItem as any;
    const sectionId = meta?._sectionId || selectedSectionIdForDetail || '';
    if (sectionId) {
      setFilters(prev => ({ ...prev, sectionIds: [sectionId], categories: [category], types: [] }));
    } else {
      setFilters(prev => ({ ...prev, categories: [category], types: [] }));
    }
    setSelectedItem(null);
    setSelectedPlace(null);
  };

  const handleTypeSelect = (type: string) => {
    const meta = selectedItem as any;
    const sectionId = meta?._sectionId || selectedSectionIdForDetail || '';
    const category = selectedItem?.category || '';
    if (sectionId && category) {
      setFilters(prev => ({ ...prev, sectionIds: [sectionId], categories: [category], types: [type] }));
    } else {
      setFilters(prev => ({ ...prev, types: [type] }));
    }
    setSelectedItem(null);
    setSelectedPlace(null);
  };

  if (configLoading || translationsLoading) return <LoadingIndicator />;

  if (!config || !translations) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <button onClick={() => navigate('/')} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors">
          Retour
        </button>
      </div>
    );
  }

  const title = EXPLORE_NAMES[i18n.language] || EXPLORE_NAMES.fr;
  const itemsInActiveSection = allSectionItems.filter((item: any) => item._sectionId === selectedSectionIdForDetail);

  return (
    <ErrorBoundary componentName="ExploreSection" sectionId="explore" sectionName="Explorer">
      <div
        className="min-h-screen"
        style={{ backgroundColor: resolveColor(config['sectionBack-color'], config, '#f9fafb') }}
      >
        <SectionHeader
          config={config}
          title={title}
          onBackClick={handleBackClick}
          translations={translations}
        />

        <div className="filter-block">
          <SearchAndFilterBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onFiltersOpen={() => setIsFilterDrawerOpen(true)}
            activeFiltersCount={activeFiltersCount}
            config={config}
            translations={translations}
            resultCount={filteredItems.length}
          />
        </div>

        <main className="main-content">
          <AnimatePresence mode="wait">
            {itemsLoading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="flex items-center justify-center h-64"
              >
                <LoadingIndicator config={config} translations={translations} />
              </motion.div>
            ) : view === 'list' ? (
              <motion.div
                key="list"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                <ExploreListView
                  items={listItems}
                  onItemClick={handleItemClick}
                  onSectionFilter={handleSectionFilter}
                  config={config}
                  translations={translations}
                />
              </motion.div>
            ) : (
              <motion.div
                key="map"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 pt-[94px] pb-[40px]"
                style={{ zIndex: 1 }}
              >
                <ExploreMapView
                  places={explorePlaces}
                  config={config}
                  translations={translations}
                  onItemClick={handleItemClick}
                  onPlaceClick={handlePlaceClick}
                  onSectionFilter={handleSectionFilter}
                  onSectionCategoryFilter={handleSectionCategoryFilter}
                  onSectionCategoryTypeFilter={handleSectionCategoryTypeFilter}
                  mainLocation={mainLocation}
                  mapInstanceRef={mapInstanceRef}
                  sectionLegend={sectionLegend}
                  activeSectionIds={filters.sectionIds}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </main>

        <div className="fixed left-1/2 -translate-x-1/2 z-[150] bottom-[58px]">
          <ExploreViewToggle
            view={view}
            onChange={setView}
            config={config}
            translations={translations}
          />
        </div>

        <ExploreFilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          items={allSectionItems}
          config={config}
          translations={translations}
          sections={sectionInfos}
        />

        {selectedPlace && !selectedItem && (
          <PlaceCard
            place={selectedPlace}
            onClose={() => setSelectedPlace(null)}
            onEventClick={(item) => {
              setSelectedItem(item);
              setSelectedPlace(null);
            }}
            onCategorySelect={handleCategorySelect}
            onTypeSelect={handleTypeSelect}
            config={config}
            translations={translations}
            selectedDate=""
            categoryColors={categoryColorsForDetail}
          />
        )}

        {selectedItem && (
          <DetailCard
            item={selectedItem}
            items={itemsInActiveSection as StandardItem[]}
            onClose={handleDetailClose}
            onItemChange={handleItemChange}
            onCategorySelect={handleCategorySelect}
            onTypeSelect={handleTypeSelect}
            config={config}
            translations={translations}
            tableId={selectedSectionIdForDetail}
            categoryColors={categoryColorsForDetail}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

const ExploreViewToggle: React.FC<{
  view: 'map' | 'list';
  onChange: (view: 'map' | 'list') => void;
  config: any;
  translations: Record<string, Record<string, string>>;
}> = ({ view, onChange, config, translations }) => {
  const { i18n } = useTranslation();
  const translatedMap = translate(translations, 'view_map', i18n.language);
  const translatedList = translate(translations, 'view_list', i18n.language);

  return (
    <div className="flex bg-white rounded-full overflow-hidden shadow-lg" style={{ padding: '4px' }}>
      <button
        onClick={() => onChange('map')}
        className="flex items-center px-4 sm:px-6 py-1.5 sm:py-2 rounded-full transition-all duration-200"
        style={{
          backgroundColor: view === 'map' ? config['major-color'] : 'transparent',
          color: view === 'map' ? '#fff' : '#000'
        }}
      >
        <MapIcon size={16} className="mr-1.5 sm:mr-2" />
        <span className="text-xs sm:text-sm font-medium">{translatedMap}</span>
      </button>
      <button
        onClick={() => onChange('list')}
        className="flex items-center px-4 sm:px-6 py-1.5 sm:py-2 rounded-full transition-all duration-200"
        style={{
          backgroundColor: view === 'list' ? config['major-color'] : 'transparent',
          color: view === 'list' ? '#fff' : '#000'
        }}
      >
        <List size={16} className="mr-1.5 sm:mr-2" />
        <span className="text-xs sm:text-sm font-medium">{translatedList}</span>
      </button>
    </div>
  );
};

export default ExploreSection;
export { ExploreSection };
